import React, { useCallback, useRef, useState } from "react";
import {
  Card,
  CardHeader,
  Container,
} from "reactstrap";
import BreadCrumb from "components/common/BreadCrumb";
import DeleteModal from "components/common/DeleteModal";
import { getDocumentTitle } from "utils/helpers";
import type { Holiday } from "features/Holidays/types/holiday.types";
import { deleteHoliday } from "features/Holidays/services/HolidayService";
import { HolidayModal } from "features/Holidays/HolidayForm/HolidayModal";
import { WeeklyOffsModal } from "features/Holidays/HolidayForm/WeeklyOffsModal";
import { toast } from "react-toastify";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { HolidayCalendarView } from "./HolidayCalendarView";
import { HolidayListView } from "./HolidayListView";

type ViewMode = "calendar" | "list";

const HolidayList = () => {
  document.title = getDocumentTitle("Holidays");
  const { can } = useAccess();
  const canCreate = can(Permissions.HOLIDAY.CREATE);
  const canUpdate = can(Permissions.HOLIDAY.UPDATE);
  const canDelete = can(Permissions.HOLIDAY.DELETE);

  const [viewMode, setViewMode] = useState<ViewMode>("calendar");

  // Refs to trigger refresh in each view
  const calRefreshRef = useRef<(() => void) | null>(null);
  const listRefreshRef = useRef<(() => void) | null>(null);

  const refreshAll = useCallback(() => {
    calRefreshRef.current?.();
    listRefreshRef.current?.();
  }, []);

  // Shared modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Holiday | null>(null);
  const [defaultDate, setDefaultDate] = useState<string | undefined>();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Holiday | null>(null);
  const [weeklyOffsOpen, setWeeklyOffsOpen] = useState(false);

  const openEdit = useCallback((holiday: Holiday) => {
    setEditing(holiday);
    setDefaultDate(undefined);
    setModalOpen(true);
  }, []);

  const openCreateAt = useCallback((date: string) => {
    setEditing(null);
    setDefaultDate(date);
    setModalOpen(true);
  }, []);

  const openCreate = useCallback(() => {
    setEditing(null);
    setDefaultDate(undefined);
    setModalOpen(true);
  }, []);

  const openDelete = useCallback((holiday: Holiday) => {
    setToDelete(holiday);
    setDeleteOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditing(null);
    setDefaultDate(undefined);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!toDelete) return;
    try {
      const res = await deleteHoliday(toDelete.id);
      if (res.success) {
        toast.success(res.message);
        setModalOpen(false);
        setEditing(null);
        refreshAll();
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? err?.message ?? "Delete failed",
      );
    } finally {
      setDeleteOpen(false);
      setToDelete(null);
    }
  };

  return (
    <React.Fragment>
      <WeeklyOffsModal
        show={weeklyOffsOpen}
        year={new Date().getFullYear()}
        onClose={() => setWeeklyOffsOpen(false)}
        onSuccess={refreshAll}
      />
      <HolidayModal
        show={modalOpen}
        editing={editing}
        defaultDate={defaultDate}
        onClose={closeModal}
        onSuccess={() => {
          closeModal();
          refreshAll();
        }}
        onDelete={
          canDelete && editing
            ? () => openDelete(editing)
            : undefined
        }
      />
      <DeleteModal
        show={deleteOpen}
        onDeleteClick={handleDeleteConfirm}
        onCloseClick={() => {
          setDeleteOpen(false);
          setToDelete(null);
        }}
        recordId={toDelete?.name}
      />

      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Holidays" pageTitle="HR Management" />
          <Card>
            <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2 border-bottom">
              <div>
                <h5 className="card-title mb-1">Holidays</h5>
                <p className="text-muted small mb-0">
                  Public, company, and optional holidays used when finalizing
                  attendance.
                </p>
              </div>
              <div className="d-flex gap-2 align-items-center flex-wrap">
                {canCreate ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-soft-primary btn-sm"
                      onClick={() => setWeeklyOffsOpen(true)}
                    >
                      <i className="ri-calendar-check-line align-bottom me-1" />
                      Generate weekly offs
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={openCreate}
                    >
                      <i className="ri-add-line align-bottom me-1" />
                      Add holiday
                    </button>
                  </>
                ) : null}
                <div className="btn-group" role="group">
                  <button
                    type="button"
                    className={`btn btn-sm ${viewMode === "calendar" ? "btn-secondary" : "btn-outline-secondary"}`}
                    onClick={() => setViewMode("calendar")}
                    title="Calendar view"
                  >
                    <i className="ri-calendar-line" />
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${viewMode === "list" ? "btn-secondary" : "btn-outline-secondary"}`}
                    onClick={() => setViewMode("list")}
                    title="List view"
                  >
                    <i className="ri-list-check" />
                  </button>
                </div>
              </div>
            </CardHeader>

            {viewMode === "calendar" ? (
              <HolidayCalendarView
                canCreate={canCreate}
                canUpdate={canUpdate}
                canDelete={canDelete}
                onEdit={openEdit}
                onCreateAt={openCreateAt}
                refreshRef={calRefreshRef}
              />
            ) : (
              <HolidayListView
                canUpdate={canUpdate}
                canDelete={canDelete}
                onEdit={openEdit}
                onDelete={openDelete}
                refreshRef={listRefreshRef}
              />
            )}
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default HolidayList;
